import React, { Component } from 'react'
import prepareComponent from 'client/shared/decorators/prepare-component'
import { withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import compose from 'lodash/fp/compose'
import cookie from 'react-cookie'
import isEmpty from 'lodash/isEmpty'
import { replace } from 'react-router-redux'
import last from 'lodash/last'
import env from 'env'
import urlFor from 'shared/tools/url-helper'
import { doesUrlEndInTitle } from 'client/bookmate/helpers/url-check-helpers'
import { serverRedirectTo } from 'client/shared/helpers/redirect-helpers'

import { load, loadEmotionRating } from 'client/bookmate/reducers/book-reducer'
import { getBookById } from 'client/bookmate/selectors/book-selector'
import { State } from 'shared/types/redux'
import { Location, Router } from 'client/shared/types/react-router'
import { CurrentUserState } from 'client/shared/types/current-user'
import { BookProps } from 'client/shared/types/book'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import Meta from 'client/shared/blocks/meta/meta'
import MetaTwinBox from 'client/shared/boxes/meta-twin-box'
import { Route } from 'client/shared/types/react-router'

const THREE_MONTHS = 3 * 30 * 24 * 60 * 60

type Props = {
  location: Location
  currentUser: CurrentUserState
  book: BookProps
  app: AppState
  router: Router
  otherVersions: BookProps[]
  routes: Route[]
} & withI18nProps

class BookPage extends Component<Props> {
  getTopAdvertPin() {
    const {
      location: {
        query: { from = '', pin = '' },
      },
    } = this.props

    if (from === 'topadvert' && pin) return pin
  }

  isNotAuthUser() {
    const {
      currentUser: { auth, completelyLoaded },
    } = this.props

    return completelyLoaded && !auth
  }

  trackTopAdvert() {
    const topAdvertPin = this.getTopAdvertPin()
    const topAdvertPinFromCookie = cookie.load('topAdvertPin')

    if (this.isNotAuthUser() && topAdvertPin && !topAdvertPinFromCookie) {
      const {
        router: { getCurrentLocation, push },
      } = this.props

      cookie.save('topAdvertPin', topAdvertPin, { maxAge: THREE_MONTHS })
      const { pathname } = getCurrentLocation()
      push({
        pathname,
        search: '',
      })
    }
  }

  componentDidUpdate() {
    this.trackTopAdvert()
  }

  render() {
    const { book, otherVersions, routes } = this.props
    let otherVersionId = ''
    if (!book.can_be_read && otherVersions?.length) {
      otherVersionId = otherVersions
        .filter(item => item.can_be_read)
        .sort((book1, book2) => book2.readers_count - book1.readers_count)[0]
        ?.uuid
    }

    return (
      <>
        {book.is_uploaded && <Meta name="robots" content="noindex" />}
        <MetaTwinBox
          customPathname={urlFor.book(otherVersionId || book.uuid)}
          language={book.language}
          {...(last(routes)?.path === 'readers' ? { robotsParams: {} } : {})}
        />
        {!isEmpty(book) && this.props.children}
      </>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { uuid, splat } = params
    const { dispatch } = store

    const cyrillicPattern = /[\u0400-\u04FF]/g

    if (!uuid) return null

    if (cyrillicPattern.test(uuid)) {
      throw { response: { status: 404 } }
    }

    const actions = [dispatch(load(uuid)), dispatch(loadEmotionRating(uuid))]

    await Promise.all(actions)

    const state = store.getState()

    const book = getBookById(state, state.book.uuid)

    if (book.resourceType === 'serial') {
      if (env.isServer()) {
        serverRedirectTo(urlFor.serial(uuid, state.app.storedQuery))
      } else {
        dispatch(replace(urlFor.serial(uuid, state.app.storedQuery)))
      }
      throw { response: { status: 404 } }
    }
    if (splat && env.isServer()) {
      if (doesUrlEndInTitle(book.title, splat)) {
        throw {
          response: {
            status: 301,
            location: urlFor.book(uuid, state.app.storedQuery),
          },
        }
      } else {
        throw { response: { status: 404 } }
      }
    }
  },
  {
    updateParams: 'uuid',
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  app: state.app,
  currentUser: state.currentUser,
  book: getBookById(state, state.book.uuid),
  otherVersions: state.book?.otherVersions?.books,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  withI18n({ update: true }),
)

export default wrappers(BookPage)
